import { Helper } from '../../../../common/helpers/Helper';

const SKIP_KEYS = {
  id: true,
  order: true,
  entityAttrName: true,
  entityAction: true,
  isStartNode: true,
  onDelete: true,
  onDuplicate: true,
  headline: true,
  lines: true,
  eventLabel: true,
  filterLines: true,
  actionLabel: true,
  detailLine: true,
  scheduleLine: true,
};

function skipKey(key) {
  if (!key || SKIP_KEYS[key]) return true;
  return false;
}

function fieldValue(source, field) {
  if (!source || !field) return undefined;
  const own = source[field.key];
  if (own != null && own !== '') return own;
  const key = String(field.key || '');
  const split = key.indexOf('__');
  if (split <= 0) return own;
  return source[key.slice(0, split)];
}

function fieldsOf(fieldDef) {
  const steps = fieldDef && fieldDef.steps;
  const fields = steps && steps[0] && steps[0].fields;
  return Array.isArray(fields) ? fields : [];
}

function optionLabel(field, storedKey) {
  const options = field && field.options;
  if (!options || !options.length || storedKey == null || storedKey === '') return '';
  for (let i = 0; i < options.length; i++) {
    const opt = options[i];
    if (!opt) continue;
    if (opt.key === storedKey || opt.value === storedKey || String(opt.key) === String(storedKey)) {
      return opt.label || opt.text || opt.title || '';
    }
  }
  return '';
}

function displayScalar(field, value) {
  if (value == null || value === '' || typeof value === 'boolean' || typeof value === 'object') return '';
  const fromOptions = optionLabel(field, value);
  if (fromOptions) return fromOptions;
  const type = field && field.type;
  const isChoice = type === 'select' || type === 'radio';
  if (isChoice && field && Array.isArray(field.options) && field.options.length) return '';
  const numberValue = Number(value);
  if (type === 'date' || type === 'dateTime' || type === 'date_yyyymmdd') {
    if (Number.isFinite(numberValue) && Math.abs(numberValue) >= 100000000000) {
      return Helper.formatTimestamp(numberValue);
    }
  }
  if (type === 'duration') {
    if (!Number.isFinite(numberValue) || numberValue === 0) return '';
    return Helper.formatDurationExact(numberValue);
  }
  if (typeof value === 'number') return '';
  const text = String(value).trim();
  if (!text) return '';
  return text;
}

function titleFromKey(key) {
  const text = String(key || '');
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function fieldTitle(field) {
  if (field && field.listConfig && field.listConfig.title) return field.listConfig.title;
  if (field && field.label) return field.label;
  return titleFromKey(field && field.key);
}

function conditionMatches(field, state) {
  if (!field || !field.condition) return true;
  const current = state || {};
  const statements = Array.isArray(field.condition.and) ? field.condition.and : [field.condition];
  for (let i = 0; i < statements.length; i++) {
    const condition = statements[i];
    if (!condition) return false;
    const conditionState = current[condition.key];
    const conditionValues = condition.values || [condition.value];
    const isIn = Array.isArray(conditionState)
      ? conditionState.some(item => Helper.inArray(conditionValues, item))
      : Helper.inArray(conditionValues, conditionState);
    const conditionOp = condition.op || 'is';
    if (conditionState === undefined) return false;
    if (conditionOp === 'is' && !isIn) return false;
    if (conditionOp === 'not' && isIn) return false;
    const hasValue = Array.isArray(conditionState) ? conditionState.length > 0 : !!conditionState;
    if (conditionOp === 'notnull' && !hasValue) return false;
  }
  return true;
}

function isPrimaryField(field) {
  if (!field || skipKey(field.key) || field.hidden || field.type === 'hidden') return false;
  if (field.advanced || field.accounts) return false;
  return true;
}

function inPrimarySection(field, state) {
  if (!isPrimaryField(field)) return false;
  return conditionMatches(field, state);
}

function isObjectRowArray(value) {
  if (!Array.isArray(value)) return false;
  for (let i = 0; i < value.length; i++) {
    const item = value[i];
    if (item && typeof item === 'object' && !Array.isArray(item)) return true;
  }
  return false;
}

function rowPhrase(row, fields) {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return '';
  const parts = [];
  const seen = {};
  (fields || []).forEach(field => {
    if (!inPrimarySection(field, row) || field.type === 'list' || seen[field.key]) return;
    if (!Object.prototype.hasOwnProperty.call(row, field.key)) return;
    const value = row[field.key];
    if (Array.isArray(value)) {
      const bits = value.map(item => displayScalar(field, item)).filter(Boolean);
      if (!bits.length) return;
      seen[field.key] = true;
      parts.push(bits.join(', '));
      return;
    }
    const text = displayScalar(field, value);
    if (!text) return;
    seen[field.key] = true;
    parts.push(text);
  });
  return parts.join(' ');
}

function pushRowPhrases(lines, rows, fields, op) {
  if (!Array.isArray(rows)) return;
  let count = 0;
  rows.forEach(row => {
    const phrase = rowPhrase(row, fields);
    if (!phrase) return;
    if (op && count > 0) lines.push({ op });
    lines.push(phrase);
    count += 1;
  });
}

export function nodePreview(data, fieldDefs, listOps) {
  const source = (data && typeof data === 'object' && !Array.isArray(data)) ? data : {};
  const defs = Array.isArray(fieldDefs) ? fieldDefs : [fieldDefs];
  const ops = listOps || {};
  const rowFields = [];
  defs.forEach(fieldDef => {
    fieldsOf(fieldDef).forEach(field => {
      if (isPrimaryField(field) && field.type !== 'list') rowFields.push(field);
    });
  });
  const seen = {};
  const blocked = {};
  let headline = '';
  const lines = [];

  defs.forEach(fieldDef => {
    fieldsOf(fieldDef).forEach(field => {
      if (!inPrimarySection(field, source) || seen[field.key]) return;
      const value = fieldValue(source, field);
      if (value == null || value === '') return;
      if (Array.isArray(value)) {
        seen[field.key] = true;
        if (isObjectRowArray(value)) {
          const configured = field.listConfig && field.listConfig.rowFields;
          let phraseFields = rowFields;
          if (configured && configured.length) phraseFields = configured;
          pushRowPhrases(lines, value, phraseFields, ops[field.key]);
          return;
        }
        const parts = value.map(item => displayScalar(field, item)).filter(Boolean);
        if (!parts.length) return;
        const text = parts.join(', ');
        const isChoice = field.type === 'select' || field.type === 'radio';
        if (!headline && isChoice) {
          headline = text;
          return;
        }
        lines.push(fieldTitle(field) + ': ' + text);
        return;
      }
      const text = displayScalar(field, value);
      if (!text) {
        if (field.options && field.options.length) blocked[field.key] = true;
        return;
      }
      if (blocked[field.key] && !optionLabel(field, value)) return;
      seen[field.key] = true;
      const isChoice = field.type === 'select' || field.type === 'radio';
      if (!headline && isChoice) {
        headline = text;
        return;
      }
      lines.push(fieldTitle(field) + ': ' + text);
    });
  });

  Object.keys(source).forEach(key => {
    if (seen[key] || skipKey(key) || String(key).indexOf('__') >= 0) return;
    const value = source[key];
    if (!isObjectRowArray(value)) return;
    seen[key] = true;
    pushRowPhrases(lines, value, rowFields, ops[key]);
  });

  return { headline, lines };
}
